import { useAuth } from '@shared/auth';
import {
  type DrawerProps,
  Box,
  Collapse,
  Drawer,
  List,
  ListItemButton,
  styled,
  Text,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { generatePath } from 'react-router-dom';

import { MENU_DRAWER_WIDTH_LARGE, MENU_DRAWER_WIDTH_SMALL } from '../../constants';
import { useGetExamCenterInformation } from '../../hooks';
import { useSidebar } from '../../providers';
import { ExamCenterSelection } from '../exam-center-selection';
import { NavMenuItem, NestedMenuItem } from '../menu-item';
import { NotificationDrawer } from '../notification-drawer';

import SidebarLogo from './sidebar-logo';
import SidebarStickyMenu from './sidebar-sticky-menu';

export const BaseSidebar = styled(Drawer, {
  name: 'Sidebar',
  slot: 'Root',
  overridesResolver: (_, styles) => [styles['root']],
  shouldForwardProp: (prop) => prop !== 'minified',
})<DrawerProps & { minified: boolean }>(({ theme, minified }) => ({
  display: 'none',
  zIndex: theme.zIndex.drawer,
  [theme.breakpoints.up('desktop')]: {
    display: 'block',
  },

  '.MuiPaper-root': {
    position: 'relative',
    width: minified ? MENU_DRAWER_WIDTH_SMALL : MENU_DRAWER_WIDTH_LARGE,
    transition: 'all 100ms ease-in-out',
  },
}));

function Sidebar() {
  const { isOpen, appMenu } = useSidebar();
  const { userProfile } = useAuth();

  const [
    isNotificationDrawerOpen,
    { open: openNotificationDrawer, close: closeNotificationDrawer },
  ] = useDisclosure(false);

  const {
    currentExamCenter,
    examCenters,
    loading: isLoading,
  } = useGetExamCenterInformation({ userId: userProfile?.id });

  const shouldHideMenu = userProfile?.status !== 'ENABLED';

  const shouldMinify = !isOpen;

  const mappedAppMenu = appMenu.map((menu) => ({
    ...menu,
    items: menu.items?.map((item) => ({
      ...item,
      link: generatePath(item?.link ?? '', { examCenterCode: currentExamCenter?.code ?? '' }),
    })),
  }));

  return (
    <>
      <BaseSidebar variant='permanent' open minified={shouldMinify}>
        <SidebarLogo minified={shouldMinify} />
        {!shouldHideMenu ? (
          <List>
            <Box sx={{ pb: (theme) => theme.spacing(1) }}>
              <Collapse in={!shouldMinify}>
                <ListItemButton disabled className='section'>
                  <Text>Exam Center</Text>
                </ListItemButton>
              </Collapse>
              <ExamCenterSelection
                minify={shouldMinify}
                currentExamCenter={currentExamCenter}
                examCenters={examCenters}
                loading={isLoading}
              />
            </Box>
            {mappedAppMenu.map((menu) => (
              <Box key={menu.section ?? menu.items?.[0]?.label}>
                <Collapse in={!shouldMinify}>
                  {menu.section ? (
                    <ListItemButton disabled className='section'>
                      <Text>{menu.section}</Text>
                    </ListItemButton>
                  ) : null}
                </Collapse>
                {menu.items?.map((item) =>
                  item.subMenu ? (
                    <NestedMenuItem key={item.label as string} {...item} minified={shouldMinify} />
                  ) : (
                    <NavMenuItem key={item.label as string} {...item} minified={shouldMinify} />
                  ),
                )}
              </Box>
            ))}
          </List>
        ) : null}
        <SidebarStickyMenu
          minified={shouldMinify}
          onClickNotificationMenuItem={openNotificationDrawer}
        />
      </BaseSidebar>
      <NotificationDrawer
        isOpen={isNotificationDrawerOpen}
        close={closeNotificationDrawer}
        shouldMinify={shouldMinify}
      />
    </>
  );
}

export default Sidebar;
